/**
 * storyGlobals — read and write `key: value` lines of the `---GLOBAL---` header.
 *
 * The header is the only place the editor touches global settings (theme, overrides).
 * Everything the panel does not know stays byte-for-byte where it was: order, unknown
 * keys, comment-less lines. The UI never parses this itself; it goes through StoryModel.
 */

export interface GlobalEntry {
  key: string;
  value: string;
}

const HEADER_OPEN = '---GLOBAL---';
const HEADER_CLOSE = '---';

/** Body lines between the fences, or [] when there is no header. */
function bodyLines(globalHeader: string): string[] {
  const source = globalHeader.replace(/\r\n/g, '\n').trim();
  if (!source.startsWith(HEADER_OPEN)) return [];
  const inner = source.slice(HEADER_OPEN.length);
  const close = inner.lastIndexOf(`\n${HEADER_CLOSE}`);
  const body = close >= 0 ? inner.slice(0, close) : inner;
  return body.split('\n').map((line) => line.replace(/^\s+$/, '')).filter((line, index, all) => !(index === 0 && line === ''));
}

export function parseGlobalEntries(globalHeader: string): GlobalEntry[] {
  const entries: GlobalEntry[] = [];
  for (const line of bodyLines(globalHeader)) {
    const colon = line.indexOf(':');
    if (colon < 1) continue;
    const key = line.slice(0, colon).trim();
    if (!key) continue;
    entries.push({ key, value: line.slice(colon + 1).trim() });
  }
  return entries;
}

export function globalsToRecord(globalHeader: string): Record<string, string> {
  const record: Record<string, string> = {};
  for (const entry of parseGlobalEntries(globalHeader)) record[entry.key] = entry.value;
  return record;
}

/**
 * Apply a patch to the header. `undefined` removes a key. Existing lines keep their
 * position; new keys are appended before the closing fence. Returns a complete header
 * (fences included) or '' when nothing is left.
 */
export function setGlobals(globalHeader: string, patch: Record<string, string | undefined>): string {
  const lines = bodyLines(globalHeader);
  const handled = new Set<string>();
  const next: string[] = [];

  for (const line of lines) {
    const colon = line.indexOf(':');
    const key = colon > 0 ? line.slice(0, colon).trim() : '';
    if (key && Object.prototype.hasOwnProperty.call(patch, key)) {
      handled.add(key);
      const value = patch[key];
      if (value === undefined || value === '') continue; // removed
      next.push(`${key}: ${value}`);
      continue;
    }
    next.push(line);
  }

  for (const [key, value] of Object.entries(patch)) {
    if (handled.has(key) || value === undefined || value === '') continue;
    next.push(`${key}: ${value}`);
  }

  const body = next.filter((line, index, all) => !(line === '' && (index === 0 || index === all.length - 1)));
  if (body.length === 0) return '';
  return `${HEADER_OPEN}\n${body.join('\n')}\n${HEADER_CLOSE}`;
}
